// Adapters: turn a colouring source into a `NodeColoring`.
//
// Each builds a prebuilt `Map<nodeId, colour>` keyed by `moduleKey` (= the
// board's `item.id`) so even the minimap — which only has the node id, not
// the Module — colours correctly. Pure + deterministic.

import type { Module, ViewProjectionPlan } from "@archlang/scene/arch-model";
import { moduleKey, getFieldString, splitCasedName } from "@archlang/scene/arch-model";
import { paletteColor } from "@archlang/scene";
import {
  buildLookup,
  type ChangeSet,
  type Status,
  type StableId as CoreStableId,
} from "@archlang/engine";
import {
  aspectColorFor,
  aspectLegend,
  aspectMemberIds,
  aspectValueOf,
} from "../../aspectColors.js";
import type { NodeColoring } from "./types.js";

function walk(modules: readonly Module[], visit: (m: Module) => void): void {
  for (const m of modules) {
    visit(m);
    walk(m.children, visit);
  }
}

// Muted tone for modules that carry no value for the active aspect — they read
// as "not classified" (recede) so the classified nodes pop, instead of keeping
// their own type colour and competing with the real classification.
const NA_COLOR = paletteColor("muted");

/**
 * Colour every module by its string value for one label key (the
 * "colour by aspect" mode). Reuses the aspect colour hash + legend so the
 * board, minimap, and legend agree. Modules with no value for `key` get a
 * distinct muted N/A colour (with its own legend row).
 */
export function buildAspectColoring(
  modules: readonly Module[],
  key: string,
): NodeColoring {
  const colors = new Map<string, string>();
  const naMembers: string[] = [];
  walk(modules, (m) => {
    const id = moduleKey(m);
    const val = aspectValueOf(m, key);
    if (val !== undefined) colors.set(id, aspectColorFor(val));
    else {
      colors.set(id, NA_COLOR);
      naMembers.push(id);
    }
  });
  const legend = aspectLegend(modules, key).map((e) => ({
    id: e.value,
    label: e.value,
    color: e.color,
    memberIds: aspectMemberIds(modules, key, e.value),
  }));
  if (naMembers.length > 0) {
    legend.push({ id: "__na__", label: "N/A", color: NA_COLOR, memberIds: naMembers });
  }
  return {
    colorForNode: (id) => colors.get(id),
    legend,
    title: key,
  };
}

/**
 * The DEFAULT colouring: every module by its type. A module inherits its type's
 * `widget.color` (types `cascade` it onto instances), so reading that field
 * reproduces EXACTLY the colour the board already shows — `colorForNode`
 * re-applies the same value (no visual change), it just surfaces a legend that
 * groups nodes by type. Modules with no explicit/cascaded colour return
 * undefined → they keep their built-in element default untouched.
 */
export function buildTypeColoring(modules: readonly Module[]): NodeColoring {
  const colors = new Map<string, string>();
  const byType = new Map<string, { color: string; members: string[] }>();
  walk(modules, (m) => {
    const named = getFieldString(m, "widget.color");
    if (!named) return;
    // `widget.color` carries a palette NAME (or a literal); resolve it exactly
    // as the card composer does, so board stroke, minimap and legend agree.
    const color = paletteColor(named);
    const id = moduleKey(m);
    colors.set(id, color);
    const type = m.typeName || "module";
    const g = byType.get(type);
    if (g) g.members.push(id);
    else byType.set(type, { color, members: [id] });
  });
  const legend = [...byType.entries()]
    .map(([type, g]) => ({
      id: type,
      label: splitCasedName(type),
      color: g.color,
      memberIds: g.members,
    }))
    .sort((a, b) => b.memberIds.length - a.memberIds.length || a.label.localeCompare(b.label));
  return { colorForNode: (id) => colors.get(id), legend, title: "Type" };
}

// Diff status palette — green/red/amber/blue for added/removed/modified/
// renamed, tuned to the JetBrains Islands Dark accents. Concrete hex (not
// theme vars) because these flow into SVG `fill`/`stroke` *attributes* (board
// stroke + minimap), which don't resolve CSS `var()`.
const DIFF_COLOR: Record<Exclude<Status, "unchanged">, string> = {
  added: "#5fad65",
  removed: "#db5c5c",
  modified: "#d6a052",
  renamed: "#3574f0",
};
const DIFF_LABEL: Record<Exclude<Status, "unchanged">, string> = {
  added: "Added",
  removed: "Removed",
  modified: "Modified",
  renamed: "Renamed",
};
const DIFF_ORDER: readonly Exclude<Status, "unchanged">[] = [
  "added",
  "modified",
  "renamed",
  "removed",
];

/**
 * Colour every changed module by its diff status. Self-contained — builds
 * its own lookup from the change set, so no DiffContext is needed for node
 * colour. `modules` is the union of before+after modules drawn on the board.
 */
export function buildDiffColoring(
  modules: readonly Module[],
  changeSet: ChangeSet,
): NodeColoring {
  const lookup = buildLookup(changeSet);
  const colors = new Map<string, string>();
  const membersByStatus = new Map<Exclude<Status, "unchanged">, string[]>();
  walk(modules, (m) => {
    if (!m.id) return;
    const status = lookup.entityStatus({
      kind: "module",
      id: m.id as unknown as CoreStableId,
    });
    if (status === "unchanged") return;
    const id = moduleKey(m);
    colors.set(id, DIFF_COLOR[status]);
    const list = membersByStatus.get(status);
    if (list) list.push(id);
    else membersByStatus.set(status, [id]);
  });
  const legend = DIFF_ORDER.filter((s) => membersByStatus.has(s)).map((s) => ({
    id: s,
    label: DIFF_LABEL[s],
    color: DIFF_COLOR[s],
    memberIds: membersByStatus.get(s) ?? [],
  }));
  return {
    colorForNode: (id) => colors.get(id),
    legend,
    title: "Changes",
  };
}

/**
 * Colour a two-point roadmap delta, reusing the diff vocabulary.
 *
 * Both scrub points read the SAME committed text, so nothing is added, removed
 * or renamed — only born, retired, or entering deprecation. Mapping those onto
 * added/removed/modified is deliberate: a roadmap delta should look exactly like
 * a change diff, because to a reader it IS one, just across time instead of
 * across commits.
 *
 * Keys arrive already board-keyed (`lifecycleWindowsOf` publishes every alias),
 * so this needs no module walk of its own.
 */
export function buildLifecycleDeltaColoring(
  delta: ReadonlyMap<string, "born" | "retired" | "deprecating">,
): NodeColoring {
  const asStatus: Record<"born" | "retired" | "deprecating", Exclude<Status, "unchanged">> = {
    born: "added",
    retired: "removed",
    deprecating: "modified",
  };
  const label: Record<"born" | "retired" | "deprecating", string> = {
    born: "Arrives",
    retired: "Retires",
    deprecating: "Deprecated",
  };
  const colors = new Map<string, string>();
  const members = new Map<"born" | "retired" | "deprecating", string[]>();
  for (const [id, kind] of delta) {
    colors.set(id, DIFF_COLOR[asStatus[kind]]);
    const list = members.get(kind);
    if (list) list.push(id);
    else members.set(kind, [id]);
  }
  const order: readonly ("born" | "retired" | "deprecating")[] = ["born", "deprecating", "retired"];
  return {
    colorForNode: (id) => colors.get(id),
    legend: order
      .filter((k) => members.has(k))
      .map((k) => ({
        id: k,
        label: label[k],
        color: DIFF_COLOR[asStatus[k]],
        memberIds: members.get(k) ?? [],
      })),
    title: "Roadmap",
  };
}

/**
 * Colour by the ACTIVE VIEW's `style { color: <cast> }` output (S15). The plan's
 * `colorResult`/`legend` are keyed on ORIGIN qualified names; `nodeIdsByOrigin`
 * (from the scene `applyProjection`) bridges each origin to the board node id(s)
 * actually rendering it — the clone's `#id`, its projected qualified key, or
 * every copy id of a `group by` fan-out. Rows pass through UNFILTERED — swatch,
 * gradient (heat ramp), "styled" literal, and "no data" alike — so the legend
 * never lies (VIEWS.md §5). Returns null when the view styles no colors, so the
 * aspect/type coloring stays in charge.
 */
export function buildViewColoring(
  plan: Pick<ViewProjectionPlan, "colorResult" | "legend">,
  nodeIdsByOrigin: ReadonlyMap<string, readonly string[]> | null,
  title?: string,
): NodeColoring | null {
  if (plan.colorResult.size === 0 && plan.legend.length === 0) return null;
  // No bridge entry → the qualified key IS the board id (an id-less module at
  // its natural place keys identically on both sides).
  const idsOf = (qual: string): readonly string[] =>
    nodeIdsByOrigin?.get(qual) ?? [qual];
  const colors = new Map<string, string>();
  for (const [qual, token] of plan.colorResult) {
    // A cast emits a palette NAME (`colorize`/`distinct`) or a literal colour
    // (`css`/`heat` ramp); `paletteColor` handles both and is idempotent.
    const color = paletteColor(token);
    for (const id of idsOf(qual)) colors.set(id, color);
  }
  const legend = plan.legend.map((e) => ({
    id: e.id,
    label: e.label,
    color: e.color === undefined ? undefined : paletteColor(e.color),
    gradient: e.gradient,
    memberIds: e.memberKeys.flatMap((k) => idsOf(k)),
  }));
  return {
    colorForNode: (id) => colors.get(id),
    legend,
    title: title ?? "View",
  };
}
